import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarLayout from './SidebarLayout';
import './shopStyles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';

const SecuritySettings = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError('');
      
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('All fields are required');
        return;
      }
      
      if (passwordData.newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters');
        return;
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }
      
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/users/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showAlert('Password changed successfully', 'success');
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to change password';
      setPasswordError(errorMsg);
    }
  };

  return (
    <SidebarLayout>
      <div className="bookings-container settings-page">
        <button
          onClick={() => navigate('/dashboard/settings')}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            transition: 'all 0.2s',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <span>←</span>
          <span>Back to Settings</span>
        </button>

        {alert.show && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        <div className="bookings-header">
          <div>
            <h1 className="bookings-title" style={{ fontSize: isMobile ? '24px' : '28px' }}>Account Security</h1>
            <p className="bookings-subtitle" style={{ fontSize: isMobile ? '14px' : '16px' }}>Manage your account security settings</p>
          </div>
        </div>

        <div className="settings-section">
          <div className="security-section">
            <h3>Change Password</h3>
            <p className="form-hint">Update your password to keep your account secure</p>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowPasswordModal(true)}
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              Change Password
            </button>
          </div>

          <div className="security-section">
            <h3>Two-Factor Authentication</h3>
            <p className="form-hint">Add an extra layer of security to your account</p>
            <span className="status-badge inactive">Coming Soon</span>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)} style={{ overflowY: 'auto' }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            width: isMobile ? '95%' : 'auto',
            maxWidth: isMobile ? '95%' : '500px',
            margin: isMobile ? '20px auto' : '50px auto',
            maxHeight: isMobile ? '90vh' : 'auto',
            overflowY: isMobile ? 'auto' : 'visible'
          }}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {passwordError && (
                <div className="alert alert-error">
                  {passwordError}
                </div>
              )}
              
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="modal-footer" style={{
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? '12px' : '0'
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                }}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleChangePassword}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default SecuritySettings;
